import React, { useCallback, useEffect, useState } from 'react';
import {
  NodeViewComponentProps,
  useCommands,
  useEditorState,
} from '@remirror/react';
import debounce from 'lodash/debounce';
import { Spinner } from '@cardboard-ui/react';
import { TodoCard } from 'apps/TenantApp/screens/Todo/components/TodoCard';
import { graphql, useMutation } from 'utils/graphClient';
import { EditorTodo_createTodo_Mutation } from './__generated__/EditorTodo_createTodo_Mutation.graphql';
import { EditorTodo_updateTodo_Mutation } from './__generated__/EditorTodo_updateTodo_Mutation.graphql';
import { useOpenItemViewer } from 'apps/TenantApp/screens/ItemViewer/useOpenItemViewer';

const createTodoMutation = graphql`
  mutation EditorTodo_createTodo_Mutation($input: TodosCreateMutationInput!) {
    createTodo(input: $input) {
      todoCreated {
        id
        __typename
        ...TodoDueDate_todo
        ...TodoAssignee_todo
        text
        documentNode {
          id
          ...TodoCard_page
        }
        assignee {
          id
        }
        state
        dueDate
        publishState
        isListed
      }
    }
  }
`;

const updateTodoMutation = graphql`
  mutation EditorTodo_updateTodo_Mutation($input: TodosUpdateMutationInput!) {
    updateTodo(input: $input) {
      todoUpdated {
        id
        __typename
        ...TodoDueDate_todo
        ...TodoAssignee_todo
        text
        documentNode {
          id
          ...TodoCard_page
        }
        assignee {
          id
        }
        state
        dueDate
        publishState
        isListed
      }
    }
  }
`;

export function EditorTodo(props: NodeViewComponentProps) {
  const [originalJSON] = useState(JSON.stringify(props.node.toJSON()));
  const { isEditModeEnabled } = useOpenItemViewer();

  const state = useEditorState();
  const commands = useCommands();

  const [createTodo, isCreatingTodo] =
    useMutation<EditorTodo_createTodo_Mutation>(createTodoMutation);
  const [updateTodo, isUpdatingTodo] =
    useMutation<EditorTodo_updateTodo_Mutation>(updateTodoMutation);

  const debounceContent = useCallback(
    debounce((id: string, content: string) => {
      updateTodo({
        variables: {
          input: { id, text: content },
        },
      });
    }, 350),
    [],
  );

  const getPosition = props.getPosition as () => number;
  const setTodoNodeId = (id: string) =>
    commands.setTodoNodeId(getPosition(), id);

  const { node, forwardRef } = props;

  useEffect(() => {
    if (!node.attrs.todoNodeId) {
      createTodo({
        variables: {
          input: {
            pageId: node.attrs.pageId,
          },
        },
        onCompleted: (data) => {
          if (data.createTodo?.todoCreated.id) {
            setTodoNodeId(data.createTodo.todoCreated.id);
          }
        },
      });
    }
  }, []);

  useEffect(() => {
    const json = JSON.stringify(node.toJSON());

    if (props.view.editable && node.attrs.todoNodeId && json !== originalJSON) {
      debounceContent(node.attrs.todoNodeId, json);
    }
  }, [node.content, node.attrs]);

  const deleteEditorTodo = () => {
    commands.deleteTodo(getPosition());
  };

  return (
    <TodoCard
      editor={{
        editMode: isEditModeEnabled,
        deleteEditorTodo,
      }}
      id={node.attrs.todoNodeId}
    >
      {node.attrs.todoNodeId ? (
        <p style={{ padding: '0px', cursor: 'text' }} ref={forwardRef} />
      ) : (
        <Spinner />
      )}
    </TodoCard>
  );
}
